import { addDays, isValidDate } from '../time/date';
import { MINUTES_PER_DAY } from './constants';

/** What the plan helpers need from a check-in item record. */
export interface CheckinPlanItem {
  readonly id: string;
  readonly data: { readonly sort: number; readonly plannedMin?: number | null };
}

/**
 * Planned time of day of a check-in item as wall-clock minutes (0 = 00:00), or null. A value
 * that is not an integer in 0–1439 reads as null instead of rejecting the record (data
 * dictionary §3, checkinItem.plannedMin).
 */
export function readPlannedMin(data: { readonly plannedMin?: unknown }): number | null {
  const value = data.plannedMin;
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < MINUTES_PER_DAY
    ? value
    : null;
}

/** Whether `value` may be written as a planned time (an integer 0–1439). */
export function isValidPlannedMin(value: unknown): value is number {
  return readPlannedMin({ plannedMin: value }) !== null;
}

/**
 * One closed version of an item's planned time (docs/requirements/checkin-plan-change.md §4):
 * it applies to occurrences whose planned instant falls on a calendar day earlier than `before`.
 */
export interface PlanVersion {
  /** Calendar day `YYYY-MM-DD`, exclusive. */
  readonly before: string;
  readonly plannedMin: number | null;
}

/**
 * The readable closed versions of `planHistory`, ascending. A value that is not an array reads
 * as no history; an entry with an invalid `before` or planned time, or a `before` not later than
 * the previous kept entry, is skipped (data dictionary §3, never rejecting the record).
 */
export function readPlanHistory(data: { readonly planHistory?: unknown }): PlanVersion[] {
  if (!Array.isArray(data.planHistory)) return [];
  const versions: PlanVersion[] = [];
  for (const entry of data.planHistory as unknown[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { before, plannedMin } = entry as { before?: unknown; plannedMin?: unknown };
    if (typeof before !== 'string' || !isValidDate(before)) continue;
    if (plannedMin !== null && plannedMin !== undefined && !isValidPlannedMin(plannedMin)) continue;
    const last = versions[versions.length - 1];
    if (last !== undefined && before <= last.before) continue;
    versions.push({ before, plannedMin: plannedMin ?? null });
  }
  return versions;
}

/**
 * The calendar day of an occurrence on `personalDate` planned at `plannedMin`: the next day when
 * the planned time is earlier than the day start (the same rule as `plannedInstantOf`).
 */
function calendarDayOf(personalDate: string, plannedMin: number | null, dayStartMin: number): string {
  return plannedMin !== null && plannedMin < dayStartMin ? addDays(personalDate, 1) : personalDate;
}

/**
 * The planned time in effect on `personalDate` (time-drift D2): the first closed version whose
 * boundary lies after the occurrence's calendar day, otherwise the current `plannedMin`. Only
 * date-string arithmetic, so the result does not depend on a time zone (D3, D8), and the
 * calendar-day boundary keeps it stable when the day start changes (D1).
 */
export function plannedMinOn(
  data: { readonly plannedMin?: unknown; readonly planHistory?: unknown },
  personalDate: string,
  dayStartMin: number,
): number | null {
  for (const version of readPlanHistory(data)) {
    if (calendarDayOf(personalDate, version.plannedMin, dayStartMin) < version.before) return version.plannedMin;
  }
  return readPlannedMin(data);
}

/**
 * The first personal date of the current version (the one stored in `plannedMin`), or null when
 * there is no closed version. The settings list notes it when it lies after today.
 */
export function currentPlanStart(
  data: { readonly planHistory?: unknown },
  dayStartMin: number,
): string | null {
  const last = readPlanHistory(data).at(-1);
  if (last === undefined) return null;
  // The last closed version covers personal date d while calendarDayOf(d) < before.
  return last.plannedMin !== null && last.plannedMin < dayStartMin ? addDays(last.before, -1) : last.before;
}

/**
 * The `planHistory` to store when the planned time changes to `nextPlannedMin` from personal
 * date `fromDate` on (data dictionary §3): the value in effect the day before `fromDate` closes
 * at `fromDate`'s calendar-day boundary, versions at or after that boundary are dropped, equal
 * neighbours merge, and trailing versions equal to the new current value fall away. Returns
 * null when no history remains. Changing twice from the same date replaces the first change.
 */
export function planHistoryAfterChange(
  data: { readonly plannedMin?: unknown; readonly planHistory?: unknown },
  fromDate: string,
  nextPlannedMin: number | null,
  dayStartMin: number,
): PlanVersion[] | null {
  const previous = plannedMinOn(data, addDays(fromDate, -1), dayStartMin);
  const boundary = calendarDayOf(fromDate, previous, dayStartMin);
  const versions = [
    ...readPlanHistory(data).filter((version) => version.before < boundary),
    { before: boundary, plannedMin: previous },
  ];
  // An earlier neighbour with the same value is covered by the later one (same planned time,
  // same calendar-day rule), so it adds nothing.
  const merged = versions.filter(
    (version, index) => versions[index + 1]?.plannedMin !== version.plannedMin,
  );
  while (merged.length > 0 && merged[merged.length - 1]!.plannedMin === nextPlannedMin) merged.pop();
  return merged.length === 0 ? null : merged;
}

/**
 * Order of the items on the check-in card (2026-10b §8.1): planned items first by
 * `(plannedMin − dayStartMin + 1440) mod 1440`, unplanned ones after by `sort`; equal keys by
 * `sort`, then by record id. Returns a new array.
 */
export function sortCheckinItems<T extends CheckinPlanItem>(
  items: readonly T[],
  dayStartMin: number,
  plannedOf: (item: T) => number | null = (item) => readPlannedMin(item.data),
): T[] {
  const keyOf = (item: T): number | null => {
    const planned = plannedOf(item);
    return planned === null ? null : (planned - dayStartMin + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  };
  return items
    .map((item) => ({ item, key: keyOf(item) }))
    .sort((a, b) => {
      if (a.key !== b.key) {
        if (a.key === null) return 1;
        if (b.key === null) return -1;
        return a.key - b.key;
      }
      if (a.item.data.sort !== b.item.data.sort) return a.item.data.sort - b.item.data.sort;
      return a.item.id < b.item.id ? -1 : a.item.id > b.item.id ? 1 : 0;
    })
    .map(({ item }) => item);
}
